import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { UserPlus, UserCheck, UserMinus, Clock } from 'lucide-react';
import { apiClient } from '../App';
import { toast } from 'sonner';

/**
 * Unified Follow/Following/Requested toggle button used EVERYWHERE.
 * 
 * Uses optimistic UI: state updates instantly on tap, rolls back on API failure.
 * 
 * States:
 * - 'none' = no relationship, show "Follow"
 * - 'following' = accepted follow, show "Following" → hover: "Unfollow"
 * - 'requested' = pending request, show "Requested"
 */
const PlayerFollowButton = ({ 
  userId, 
  username, 
  isFollowing: propIsFollowing = false,
  isRequested: propIsRequested = false,
  onFollowChange,
  size = 'sm',
  variant = 'compact',
  className = ''
}) => {
  const [state, setState] = useState(() => {
    if (propIsFollowing) return 'following';
    if (propIsRequested) return 'requested';
    return 'none';
  });
  const [busy, setBusy] = useState(false);
  const [hovered, setHovered] = useState(false);

  // Sync internal state with props when they change
  useEffect(() => {
    if (propIsFollowing) {
      setState('following');
    } else if (propIsRequested) {
      setState('requested');
    } else {
      setState('none');
    }
  }, [propIsFollowing, propIsRequested]);

  const handleClick = async (e) => {
    e.stopPropagation();
    if (busy) return;
    
    const prevState = state;
    setBusy(true);

    if (state === 'following' || state === 'requested') {
      // Optimistic: immediately show "Follow"
      setState('none');
      if (onFollowChange) {
        onFollowChange(userId, { following: false, requested: false });
      }

      try {
        await apiClient.delete(`/follow/${userId}`);
        toast.success(prevState === 'following' ? `Unfollowed ${username}` : 'Follow request cancelled');
      } catch (error) {
        // Rollback
        setState(prevState);
        if (onFollowChange) {
          onFollowChange(userId, {
            following: prevState === 'following',
            requested: prevState === 'requested',
          });
        }
        console.error('Follow action failed:', error);
        toast.error('Something went wrong');
      } finally {
        setBusy(false);
      }
    } else {
      // Optimistic: immediately show "Requested" (safe default — most profiles are not auto-accept)
      setState('requested');
      if (onFollowChange) {
        onFollowChange(userId, { following: false, requested: true });
      }

      try {
        const response = await apiClient.post(`/follow/${userId}`);
        const data = response.data;

        // Reconcile with actual server response
        if (data.following) {
          setState('following');
          toast.success(`Following ${username}`);
          if (onFollowChange) {
            onFollowChange(userId, { following: true, requested: false });
          }
        } else if (data.request_pending) {
          // Already in 'requested' state — just show toast
          toast.success(`Follow request sent to ${username}`);
        }
      } catch (error) {
        // Rollback
        setState('none');
        if (onFollowChange) {
          onFollowChange(userId, { following: false, requested: false });
        }
        console.error('Follow action failed:', error);
        if (error.response?.status === 400 && error.response?.data?.detail?.includes('yourself')) {
          return;
        }
        if (error.response?.status === 403) {
          toast.error('Cannot follow this user');
        } else {
          toast.error('Something went wrong');
        }
      } finally {
        setBusy(false);
      }
    }
  };

  const isProfile = variant === 'profile';

  const sizeClasses = isProfile
    ? 'h-9 px-4 text-sm'
    : size === 'sm' 
      ? 'h-7 px-2.5 text-xs' 
      : 'h-9 px-3 text-sm';

  const tapFeedback = 'transition-transform duration-100 active:scale-[0.98] active:brightness-95';

  // FOLLOWING state
  if (state === 'following') {
    const showUnfollow = hovered;
    return (
      <Button
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        variant="outline"
        className={`${sizeClasses} rounded-full font-medium transition-colors ${tapFeedback} ${
          showUnfollow
            ? 'text-red-600 border-red-300 hover:bg-red-50'
            : 'text-zinc-600 border-zinc-300 hover:bg-zinc-50 hover:text-zinc-800'
        } ${className}`}
        data-testid={`following-btn-${userId}`}
      >
        {showUnfollow ? (
          <UserMinus className="w-3.5 h-3.5 mr-1" />
        ) : (
          <UserCheck className="w-3.5 h-3.5 mr-1" />
        )}
        {showUnfollow ? 'Unfollow' : 'Following'}
      </Button>
    );
  }

  // REQUESTED state
  if (state === 'requested') {
    return (
      <Button
        onClick={handleClick}
        variant="outline"
        className={`${sizeClasses} rounded-full font-medium text-orange-600 border-orange-300 hover:bg-orange-50 ${tapFeedback} ${className}`}
        data-testid={`requested-btn-${userId}`}
      >
        <Clock className="w-3.5 h-3.5 mr-1" />
        Requested
      </Button>
    );
  }

  // NONE state — show Follow (quiet outline so it doesn't compete with primary CTAs)
  return (
    <Button
      onClick={handleClick}
      variant="outline"
      className={`${sizeClasses} rounded-full font-medium bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50 hover:border-zinc-400 hover:text-zinc-900 ${tapFeedback} ${className}`}
      data-testid={`follow-btn-${userId}`}
    >
      <UserPlus className="w-3.5 h-3.5 mr-1" />
      Follow
    </Button>
  );
};

export default PlayerFollowButton;
